import React, { useEffect, useState } from 'react';
import { Modal } from '@mui/material';
import p21Logo from '../../../assets/images/p21-logo.png';
import { AppInputField } from '../AppInputField/AppInputField';
import { AppButton } from '../AppButton/AppButton';
import { DillCheckbox } from '../Dill/DillCheckbox';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { addP21SupplierConnection } from '../../../redux/services/suppliersService';
import { useNavigate } from 'react-router-dom';
import { openMessageModal } from '../../../redux/globalSlices/genericSlice';
import { buildSupplierPath } from '../../../utils/helpers';

const AppP21ConnectionModal = ({
  open,
  handleClose = () => {},
}: {
  open: boolean;
  handleClose: () => void;
}) => {
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [companyBaseUrl, setCompanyBaseUrl] = useState('');
  const [companyAPIBaseUrl, setCompanyAPIBaseUrl] = useState('');
  const [bankCoNo, setBankCoNo] = useState('');
  const [password, setPassword] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [useBillToShipToSplit, setUseBillToShipToSplit] = useState(false);

  const [errors, setErrors] = useState({
    companyId: '',
    companyBaseUrl: '',
    companyAPIBaseUrl: '',
    bankCoNo: '',
    userName: '',
    password: '',
  });
  useEffect(() => {
    if (open) {
      setErrors({
        companyId: '',
        companyBaseUrl: '',
        companyAPIBaseUrl: '',
        bankCoNo: '',
        userName: '',
        password: '',
      });
      setUserName('');
      setPassword('');
      setClientSecret('');
      setCompanyId('');
      setUseBillToShipToSplit(false);
    }

    return () => {};
  }, [open]);
  function isValidCompanyBaseUrl(url: string, hasClientSecret: boolean) {
    try {
      const parsed = new URL(url);

      const isHttps = parsed.protocol === 'https:';
      if (!isHttps || parsed.search || parsed.hash) {
        return false;
      }

      if (hasClientSecret) {
        // Self-hosted P21 instances (client-secret / client_credentials auth) can live at a
        // custom host and port, and their base url may already include the `/api` segment
        // (e.g. "https://pod.purvisbearing.com:3446/api").
        const path = parsed.pathname.replace(/\/$/, '');
        return path === '' || path === '/api';
      }

      const hasNoPath = parsed.pathname === '/';
      const hostPattern = /^[a-zA-Z0-9-]+\.epicordistribution\.com$/;
      return hasNoPath && hostPattern.test(parsed.hostname);
    } catch {
      return false;
    }
  }

  const validateInputs = () => {
    const newErrors = {
      companyId: '',
      companyBaseUrl: '',
      companyAPIBaseUrl: '',
      bankCoNo: '',
      userName: '',
      password: '',
    };

    let isValid = true;

    if (!companyBaseUrl) {
      newErrors.companyBaseUrl = 'This field is required.';
      isValid = false;
    }
    if (companyBaseUrl && !isValidCompanyBaseUrl(companyBaseUrl, !!clientSecret)) {
      newErrors.companyBaseUrl = clientSecret
        ? 'Invalid url. It should be "https://<host>[:port]" or "https://<host>[:port]/api"'
        : 'Invalid url. It should be "https://<company>.epicordistribution.com"';
      isValid = false;
    }
    if (!userName) {
      newErrors.userName = 'This field is required.';
      isValid = false;
    }
    if (!password && !clientSecret) {
      newErrors.password = 'Provide a Password or a Client Secret.';
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  const handleAuthorizeButtonClick = async () => {
    const isValid = validateInputs();
    if (!isValid) {
      return;
    }
    const res = await dispatch(
      addP21SupplierConnection({
        supplierId: currentSupplier?.id || '',
        companyId,
        companyBaseUrl,
        companyAPIBaseUrl,
        bankCoNo,
        userName,
        password: password || undefined,
        clientSecret: clientSecret || undefined,
        useBillToShipToSplit,
      })
    );
    console.log({ res });
    if (res && res.type === 'supplier/addP21SupplierConnection/fulfilled') {
      navigate(buildSupplierPath(currentSupplier?.id, '/customers?success=true'));
      navigate(0);
    } else {
      let errorMessage = '';
      if (res.type === 'supplier/addP21SupplierConnection/rejected') {
        errorMessage = res.payload as any;
      }
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Failed to connect to P21 platform',
          subTitle: errorMessage || '',
          onClose: () => {},
        })
      );
    }
  };
  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <div className="flex min-h-80 w-[30vw] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg  flex-col py-4 px-3">
        <div className="flex items-center flex-col">
          <img className="w-16 object-contain rounded-full" src={p21Logo} />
          <p className="text-sm font-bold">Prophet 21</p>
          <p className="text-xs">Connection</p>
        </div>
        <div className="flex flex-col">
          <div className="my-1.5">
            <AppInputField
              label="Company Id"
              value={companyId}
              errorText={errors.companyId ? errors.companyId : ''}
              onTextChange={(value) => {
                setCompanyId(value);
              }}
            />
          </div>
          <div className="my-1.5">
            <AppInputField
              label="Base Url"
              placeholder="https://abcd.epicordistribution.com"
              value={companyBaseUrl}
              isRequired={true}
              errorText={errors.companyBaseUrl ? errors.companyBaseUrl : ''}
              onTextChange={(value) => {
                setCompanyBaseUrl(value);
              }}
            />
          </div>
          <div className="my-1.5">
            <AppInputField
              label="API Base Url"
              placeholder="https://abcd.epicordistribution.com"
              value={companyAPIBaseUrl}
              isRequired={true}
              errorText={errors.companyAPIBaseUrl ? errors.companyAPIBaseUrl : ''}
              onTextChange={(value) => {
                setCompanyAPIBaseUrl(value);
              }}
            />
          </div>
          <div className="my-1.5">
            <AppInputField
              label="Bank Number"
              placeholder=""
              value={bankCoNo}
              errorText={errors.bankCoNo ? errors.bankCoNo : ''}
              onTextChange={(value) => {
                setBankCoNo(value);
              }}
            />
          </div>
          <div className="my-1.5">
            <AppInputField
              label="User Name"
              value={userName}
              isRequired={true}
              errorText={errors.userName ? errors.userName : ''}
              onTextChange={(value) => {
                setUserName(value);
              }}
            />
          </div>
          <div className="my-1.5">
            <AppInputField
              label="Password"
              placeholder=""
              inputType="password"
              value={password}
              errorText={errors.password ? errors.password : ''}
              onTextChange={(value) => {
                setPassword(value);
              }}
            />
          </div>
          <p className="text-xs text-center my-1">— or —</p>
          <div className="my-1.5">
            <AppInputField
              label="Client Secret"
              placeholder=""
              inputType="password"
              value={clientSecret}
              errorText={''}
              onTextChange={(value) => {
                setClientSecret(value);
              }}
            />
          </div>

          <div className="my-1.5 flex items-center gap-2">
            <DillCheckbox
              isChecked={useBillToShipToSplit}
              onChange={(checked: boolean) => setUseBillToShipToSplit(checked)}
            />
            <span className="text-sm">Create separate Bill To / Ship To accounts</span>
          </div>

          <div className="flex justify-between w-full mt-4">
            <AppButton
              buttonStyles={{ width: '100px' }}
              text="Cancel"
              type="TERTIARY"
              onClick={handleClose}
            />
            <AppButton
              buttonStyles={{ width: '100px' }}
              text="Authorize"
              onClick={handleAuthorizeButtonClick}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AppP21ConnectionModal;
